import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";

import "../../../Styles/authentication.css";
import "../../../Styles/typography.css";

import { ReactComponent as HeaderLogo } from "../../../Assets/Images/LandingPage/subjects-master-logo.svg";
import { ReactComponent as ButtonArrow } from "../../../Assets/Images/Button/btn_arrow.svg";
import { ReactComponent as FacebookIcon } from "../../../Assets/Images/Header/Facebook.svg";
import { ReactComponent as InstaIcon } from "../../../Assets/Images/Header/Instagram.svg";
import { ReactComponent as TiktokIcon } from "../../../Assets/Images/Header/TikTok.svg";
import { SOCIAL_MEDIA_LINKS, Api, URL_TEACHER_LOGIN, generateValidationSchema } from "Helpers";
import { showToast } from "Redux/General/Actions";

import MobileHeader from "Components/Header/MobileHeader";
import CustomButton from "Components/Commons/CustomButton/CustomButton";
import { InputWithLabel } from "Components/Commons";

import styled from "styled-components";
import { pilotAuthenticationBackground } from "Styles/pilotAuthentication.style";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { InputAdornment, IconButton } from "@material-ui/core";

import { InputLabel, Box } from "@material-ui/core";
import { useFormik } from "formik";

// Same page layout and styles as Reset Password, so it looks like the rest of the website's login pages.
import { PageWrapper as ResetPasswordWrapper } from "Components/Pages/ResetPassword/ResetPassword.style";

const PageWrapper = styled(ResetPasswordWrapper)`
	${pilotAuthenticationBackground}

	.reset_password_wrapper .MuiInputBase-root {
		position: relative;
		width: 100%;
	}

	.reset_password_wrapper .MuiInputBase-input {
		box-sizing: border-box;
		padding-right: 56px;
	}

	.reset_password_wrapper .MuiInputAdornment-positionEnd {
		position: absolute;
		right: 6px;
		top: 50%;
		transform: translateY(-50%);
		margin-left: 0;
	}

	.reset_password_wrapper .MuiIconButton-root {
		padding: 10px;
	}
`;

const inputs = [
	{
		label: "Password",
		name: "password",
		type: "password",
		validation: { required: true, min: 6 },
	},
	{
		label: "Confirm Password",
		name: "confirmPassword",
		type: "password",
		validation: { required: true, equals: "password" },
	},
];

const validationSchema = generateValidationSchema(inputs);

const initialValues = { password: "", confirmPassword: "" };

// The shared Api helper can leave a request unsettled when there is no response at all; stop waiting after this long.
const REQUEST_TIMEOUT_MS = 30000;

const LINK_PROBLEM_MESSAGE = "This link has expired or is no longer valid. Go to Portal Login and use \"Forgot password\" to get a new link.";

/**
 * Classroom Pilot teacher: create the first password from the link in the pilot activation email
 * (/teacher/create-password/:token). Uses the portal (CMS) reset-password endpoint, which accepts the
 * teacher's token, then sends the teacher to the Portal Login.
 */
function CreatePassword() {
	const [isLoading, setIsLoading] = useState(false);
	const [visiblePasswords, setVisiblePasswords] = useState({});
	const [linkError, setLinkError] = useState("");
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { token } = useParams();

	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit: createPassword,
	});

	async function createPassword(values) {
		setIsLoading(true);
		setLinkError("");
		const data = new FormData();
		data.append("token", token);
		data.append("newPassword", values.password);

		const request = new Api().post("user/cms/resetPassword", { data, skipAuth: true, skipErrorHandling: true, skipToast: true });
		const timeout = new Promise((resolve) => setTimeout(() => resolve({ status: 0, timedOut: true }), REQUEST_TIMEOUT_MS));

		let response;
		try {
			response = await Promise.race([request, timeout]);
		} catch (error) {
			response = error;
		}
		setIsLoading(false);

		if (response?.status === 200) {
			dispatch(showToast("Your password has been created. Please log in.", "success"));
			navigate(URL_TEACHER_LOGIN);
			return;
		}
		if (response?.timedOut || !response?.status) {
			setLinkError("We could not reach the server. Please check your connection and try again.");
			return;
		}
		// 400 / 401: the token is unknown or expired (it lasts 48 hours, and a newer email replaces it).
		setLinkError(LINK_PROBLEM_MESSAGE);
	}

	return (
		<PageWrapper>
			<div className="login-regi-wrapper">
				{/* LEFT SIDE */}
				<div className="auth-wrap-left">
					<div className="auth_wrap_logo">
						<MobileHeader />
						<Link to="/" className="desktop_logo_login">
							<HeaderLogo />
						</Link>
					</div>
					<div className="auth_wrap_left_in">
						<h1>
							Welcome to <br /> <span className="redfont">Subjects Master</span>
						</h1>
						<p>
							Your Classroom Pilot is ready. Create a password for your teacher account, then log in to set
							up your class and create your first assessment.
						</p>
					</div>
				</div>

				{/* RIGHT SIDE */}
				<div className="auth_wrap_right">
					<div className="menu_other_buttons">
						<div className="social_handles">
							<ul>
								<li>
									<a href={SOCIAL_MEDIA_LINKS.FACEBOOK} target="_blank" rel="noopener noreferrer">
										<FacebookIcon />
									</a>
								</li>
								<li>
									<a href={SOCIAL_MEDIA_LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer">
										<InstaIcon />
									</a>
								</li>
								<li>
									<a href={SOCIAL_MEDIA_LINKS.TIKTOK} target="_blank" rel="noopener noreferrer">
										<TiktokIcon />
									</a>
								</li>
							</ul>
						</div>
						<div className="portal_login_link">
							<Link to={URL_TEACHER_LOGIN}>Portal Login</Link>
						</div>
						<div className="logins_btn">
							<Link to="/login" className="customs_btn">
								Login{" "}
								<span>
									<ButtonArrow />
								</span>
							</Link>
						</div>
					</div>

					<div className="auth_right_inner">
						<div className="auth_right_head">
							<h2>Create Password</h2>
							<h4 className="redfont">Set up your teacher account.</h4>
						</div>

						<form onSubmit={formik.handleSubmit} className="reset_password_wrapper">
							<Box className="content">
								<Box>
									<Box className="flex-column-parent">
										{inputs.map((input) => (
											<Box key={input.name} className="flex-column field_wrapper">
												<Box component="div" sx={{ display: "inline" }} className="text">
													<InputLabel htmlFor={input.name} className="text">{input.label}</InputLabel>
												</Box>

												<InputWithLabel
													id={input.name}
													name={input.name}
													className="full-width"
													type={visiblePasswords[input.name] ? "text" : input.type}
													autoComplete="new-password"
													endAdornment={
															<InputAdornment position="end">
																<IconButton
																	type="button"
																	aria-label={`${visiblePasswords[input.name] ? "Hide" : "Show"} ${input.label.toLowerCase()}`}
																	aria-pressed={Boolean(visiblePasswords[input.name])}
																	onClick={() => setVisiblePasswords((current) => ({ ...current, [input.name]: !current[input.name] }))}
																	onMouseDown={(event) => event.preventDefault()}
																>
																	{visiblePasswords[input.name] ? <Visibility /> : <VisibilityOff />}
																</IconButton>
															</InputAdornment>
													}
													placeholder={input.label}
													value={formik.values[input.name]}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													signupform={true}
												/>

												<Box component="div" sx={{ display: "inline", height: "10px" }} className="validation-error text">
													{formik.touched[input.name] && formik.errors[input.name] ? formik.errors[input.name] : ""}
												</Box>
											</Box>
										))}
									</Box>
								</Box>

								{linkError && (
									<Box component="div" className="validation-error text create_password_error" role="alert">
										{linkError}
									</Box>
								)}

								<div className="login_btn_divs">
									<Box className="login-button-parent">
										<CustomButton text={isLoading ? "Loading..." : "Create Password"} className="button" type="submit" isLoading={isLoading} />
									</Box>
								</div>
							</Box>
						</form>
					</div>
				</div>
			</div>
		</PageWrapper>
	);
}

export default CreatePassword;
